import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSiteConfig } from '../../context/SiteConfigContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Loader2, CheckCircle } from 'lucide-react';
import { LOGO_FULL_FALLBACK } from './constants';

export function ResetPasswordPage() {
  const { api } = useAuth();
  const { logoUrl } = useSiteConfig();
  const LOGO_FULL = logoUrl || LOGO_FULL_FALLBACK;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (!token) { setError('Invalid reset token'); return; }

    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Password reset failed. Token may be expired.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#000B18] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          <CheckCircle className="w-16 h-16 text-[#00E676] mx-auto mb-6" />
          <h1 className="font-outfit text-2xl font-bold text-white mb-2">Password Reset</h1>
          <p className="text-[#A0B4CC] mb-6">Your password has been reset successfully.</p>
          <Link to="/login">
            <Button className="btn-primary">Go to Login</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000B18] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_FULL} alt="All Global Access" className="h-16 mx-auto mb-6" />
          </Link>
          <h1 className="font-outfit text-3xl font-bold text-white mb-2">Reset Password</h1>
          <p className="text-[#A0B4CC]">Enter your new password</p>
        </div>

        <Card className="bg-[#041A3B] border-white/10">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-md bg-[#FF1744]/10 border border-[#FF1744]/30 text-[#FF1744] text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-white">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Min 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF]"
                  data-testid="reset-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-white">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF]"
                  data-testid="reset-confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary h-11"
                data-testid="reset-submit-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-[#00E5FF] hover:underline text-sm">
                Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
